// Language context provider – v3
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { es as esDictionary } from '@/i18n/es';
import { en as enDictionary } from '@/i18n/en';
import { es as esLocale, enUS as enLocale } from 'date-fns/locale';

type Language = 'es' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
  t: (key: string) => string;
  dateLocale: typeof import('date-fns/locale').es;
}

const dictionaries: Record<Language, any> = {
  es: esDictionary,
  en: enDictionary,
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

function getInitialLanguage(): Language {
  try {
    const saved = localStorage.getItem('app-language');
    if (saved === 'es' || saved === 'en') return saved;
  } catch {}
  // Auto-detect from browser
  return navigator.language.startsWith('en') ? 'en' : 'es';
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    try { localStorage.setItem('app-language', lang); } catch {}
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguage(language === 'es' ? 'en' : 'es');
  }, [language, setLanguage]);

  const t = useCallback((key: string): string => {
    const keys = key.split('.');
    let value: any = dictionaries[language];
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return key; // Return key as fallback
      }
    }
    return typeof value === 'string' ? value : key;
  }, [language]);

  const dateLocale = useMemo(() => language === 'es' ? esLocale : enLocale, [language]);

  const contextValue = useMemo(() => ({
    language, setLanguage, toggleLanguage, t, dateLocale,
  }), [language, setLanguage, toggleLanguage, t, dateLocale]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
